import { Col, Input, InputNumber, Row, Select, Space } from "antd";
import {
  LocationTarget,
  interactiveLocationActions,
  interactiveLocationTargetTypes,
} from "@blings/blings-player";
import {
  LOCATION_TARGET_LABELS,
  replaceLocationTargetType,
  updateLocationGooglePlaceId,
  updateLocationTargetField,
} from "../../locationConnector";

const FieldLabel = ({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <span className="location-field-label">
    {children}
    {required ? " *" : ""}
  </span>
);

const CoordinateFields = ({
  target,
  onChange,
  required,
}: {
  target: LocationTarget;
  onChange: (target: LocationTarget) => void;
  required?: boolean;
}) => (
  <Row gutter={12}>
    <Col span={12}>
      <FieldLabel required={required}>Latitude</FieldLabel>
      <InputNumber
        size="large"
        min={-90}
        max={90}
        precision={6}
        placeholder="-90 to 90"
        value={(target as any).latitude}
        onChange={(value) =>
          onChange(updateLocationTargetField(target, "latitude", value))
        }
      />
    </Col>
    <Col span={12}>
      <FieldLabel required={required}>Longitude</FieldLabel>
      <InputNumber
        size="large"
        min={-180}
        max={180}
        precision={6}
        placeholder="-180 to 180"
        value={(target as any).longitude}
        onChange={(value) =>
          onChange(updateLocationTargetField(target, "longitude", value))
        }
      />
    </Col>
  </Row>
);

export const LocationTargetEditor = ({
  value,
  action,
  onChange,
}: {
  value?: LocationTarget;
  action: interactiveLocationActions;
  onChange: (value: LocationTarget) => void;
}) => {
  const target =
    value && typeof value === "object"
      ? value
      : replaceLocationTargetType(interactiveLocationTargetTypes.coordinates);
  const targetType = target.type;

  const update = (key: string, fieldValue: unknown) =>
    onChange(updateLocationTargetField(target, key, fieldValue));

  return (
    <Space direction="vertical" className="location-target-editor" size={12}>
      <label>
        <FieldLabel required>Location type</FieldLabel>
        <Select
          size="large"
          value={targetType}
          onChange={(type) => onChange(replaceLocationTargetType(type))}
          options={Object.values(interactiveLocationTargetTypes).map(
            (type) => ({
              value: type,
              label: LOCATION_TARGET_LABELS[type],
              disabled:
                type === interactiveLocationTargetTypes.query &&
                action === interactiveLocationActions.navigate,
              title:
                type === interactiveLocationTargetTypes.query &&
                action === interactiveLocationActions.navigate
                  ? "Search queries can show results but cannot start navigation."
                  : undefined,
            })
          )}
        />
      </label>

      {targetType === interactiveLocationTargetTypes.place && (
        <>
          <div className="location-editor-hint">
            Enter an exact place with coordinates. A name alone is not a precise
            destination.
          </div>
          <label>
            <FieldLabel required>Place name</FieldLabel>
            <Input
              size="large"
              value={(target as any).label}
              placeholder="Queen Victoria Building"
              onChange={(event) => update("label", event.target.value)}
              allowClear
            />
          </label>
          <CoordinateFields target={target} onChange={onChange} required />
          <label>
            <FieldLabel>Formatted address</FieldLabel>
            <Input
              size="large"
              value={(target as any).formattedAddress}
              placeholder="455 George St, Sydney NSW 2000, Australia"
              onChange={(event) =>
                update("formattedAddress", event.target.value)
              }
              allowClear
            />
          </label>
          <label>
            <FieldLabel>Google Place ID</FieldLabel>
            <Input
              size="large"
              value={(target as any).providerIds?.googlePlaceId}
              placeholder="ChIJ..."
              onChange={(event) =>
                onChange(
                  updateLocationGooglePlaceId(target, event.target.value)
                )
              }
              allowClear
            />
          </label>
        </>
      )}

      {targetType === interactiveLocationTargetTypes.coordinates && (
        <>
          <CoordinateFields target={target} onChange={onChange} required />
          <label>
            <FieldLabel>Label</FieldLabel>
            <Input
              size="large"
              value={(target as any).label}
              placeholder="Eiffel Tower"
              onChange={(event) => update("label", event.target.value)}
              allowClear
            />
          </label>
        </>
      )}

      {targetType === interactiveLocationTargetTypes.address && (
        <>
          <label>
            <FieldLabel required>Complete address</FieldLabel>
            <Input.TextArea
              rows={2}
              value={(target as any).formattedAddress}
              placeholder="1 Apple Park Way, Cupertino, CA 95014"
              onChange={(event) =>
                update("formattedAddress", event.target.value)
              }
              allowClear
            />
          </label>
          <label>
            <FieldLabel>Label</FieldLabel>
            <Input
              size="large"
              value={(target as any).label}
              placeholder="Apple Park"
              onChange={(event) => update("label", event.target.value)}
              allowClear
            />
          </label>
          <label>
            <FieldLabel>Country code</FieldLabel>
            <Input
              size="large"
              maxLength={2}
              value={(target as any).countryCode}
              placeholder="US"
              onChange={(event) =>
                update("countryCode", event.target.value.toUpperCase())
              }
              allowClear
            />
          </label>
        </>
      )}

      {targetType === interactiveLocationTargetTypes.query && (
        <>
          <label>
            <FieldLabel required>Search query</FieldLabel>
            <Input
              size="large"
              value={(target as any).query}
              placeholder="coffee"
              onChange={(event) => update("query", event.target.value)}
              allowClear
            />
          </label>
          <div className="location-editor-hint">
            Optional coordinates bias the search. Provide both or neither.
          </div>
          <CoordinateFields target={target} onChange={onChange} />
        </>
      )}
    </Space>
  );
};
